
import type { CurationToolRuleType, NextQueries } from '@empathyco/playboard-core-domain'
import type { Route } from 'vue-router'
import type { IToolingTableDataElement } from '../../composables/use-tooling-list'
import type {
  DataTableColumn,
  DataTableOption,
  GuardResolveFunction,
  ToolMessages,
} from '../../models'

import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import CrudList from '../../components/crud/crud-list.component.vue'
import DataTable from '../../components/crud/data-table.component.vue'
import { useToolingList } from '../../composables/use-tooling-list'
import { AlignEnum, FeatureIdEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'

export default defineComponent({
  name: 'NextQueriesList',
  components: {
    CrudList,
    DataTable,
  },
  async beforeRouteLeave(to: Route, from: Route, next: GuardResolveFunction) {
    this.contextWatchDisabled = true
    next()
  },
  setup(props, ctx) {
    const baseClass = 'epb-next-queries-list'

    const routes = ROUTER_CONSTS.routes[FeatureIdEnum.NEXT_QUERIES]

    const currentInstance = getCurrentInstance()
    const messages: ToolMessages = currentInstance.proxy.$epb.messages.tools.nextQueries

    const toolingList = useToolingList<NextQueries>(props, ctx, { routes, messages })

    const columns = computed<Array<DataTableColumn>>(() => {
      return [
        {
          id: 'criteria.queries',
          sortable: true,
          sortableId: 'query',
          text: messages.fields.query.label,
          width: '12rem',
          unwrapText: true,
          copyToClipboard: true,
        },
        ...toolingList.visiblePivotColumns.value,
        {
          id: 'data.rules.added',
          text: messages.fields.rules.types.add,
        },
        {
          id: 'data.rules.removed',
          text: messages.fields.rules.types.remove,
        },
        {
          id: 'metadata.updated',
          sortable: true,
          sortableId: 'updated',
          text: messages.fields.updateDate.label,
        },
        {
          id: 'metadata.author',
          text: messages.fields.user.label,
        },
        {
          id: 'criteria.enabled',
          text: messages.fields.enabled.label,
        },
        {
          id: 'options',
          width: '44px',
          align: AlignEnum.CENTER,
        },
      ]
    })

    const tableData = computed<Array<IToolingTableDataElement & { data: any }>>(() =>
      toolingList.tableData.value.map(dataItem => ({
        ...dataItem,
        data: {
          rules: {
            added: getRuleTextsByType(dataItem as unknown as NextQueries, 'ADD'),
            removed: getRuleTextsByType(dataItem as unknown as NextQueries, 'REMOVE'),
          },
        },
      })),
    )

    function getRuleTextsByType(dataItem: NextQueries, type: CurationToolRuleType): Array<string> {
      return dataItem.data.rules.reduce<Array<string>>((result, rule) => {
        if (rule.type === type) {
          result.push(rule.query)
        }
        return result
      }, [])
    }

    toolingList.loadData()

    const crudListRef = ref(null)

    function onOptionsClick(option: DataTableOption, dataItem): void {
      toolingList.onOptionClick(option, dataItem, crudListRef)
    }

    return {
      ...toolingList,
      baseClass,
      routes,
      messages,
      columns,
      tableData,
      crudListRef,
      onOptionsClick,
    }
  },
})
